import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { formatNumber, formatCurrency, formatRelativeTime } from '../../lib/utils';
import { StatusBadge } from '../ui/Badge';
import { useOfferLookup } from '../../hooks/useLookups';
import { Activity } from 'lucide-react';

interface RecentConversion {
  id: number;
  offerId: number | null;
  revenue: number;
  status: string;
  eventType: string;
  createdAt: number;
}

interface RecentConversionsProps {
  subId: string | null;
  range: string;
  customFrom?: string;
  customTo?: string;
}

export function RecentConversions({
  subId,
  range,
  customFrom,
  customTo,
}: RecentConversionsProps) {
  const offerLookup = useOfferLookup();

  const recentConversions = useQuery({
    queryKey: ['sub-detail-recent', subId ?? '', range, customFrom ?? '', customTo ?? ''],
    queryFn: () => {
      if (!subId) return [];
      return apiClient.get<RecentConversion[]>('/api/v1/conversions', {
        sub_id: subId,
        range,
        ...(range === 'custom' && customFrom && customTo
          ? { from: customFrom, to: customTo }
          : {}),
        limit: 10,
      });
    },
    enabled: !!subId,
  });

  if (!subId) {
    return null;
  }

  return (
    <div>
      <h4 className="text-[10px] font-display uppercase tracking-wider text-neon-magenta flex items-center gap-1 mb-2">
        <Activity size={12} />
        Recent Conversions
      </h4>
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded">
        {recentConversions.isLoading ? (
          <p className="text-[11px] font-mono text-text-tertiary italic py-1">
            loading recent conversions...
          </p>
        ) : (recentConversions.data?.length ?? 0) === 0 ? (
          <p className="text-[11px] font-mono text-text-tertiary italic py-1">
            no conversions for this sub_id in range
          </p>
        ) : (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-text-tertiary text-[10px] uppercase border-b border-border-subtle">
                <th className="text-left py-1.5">Time</th>
                <th className="text-left">Offer</th>
                <th className="text-left">Event</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentConversions.data!.map((rc) => (
                <tr key={rc.id} className="border-b border-border-subtle/40">
                  <td
                    className="py-1.5 text-text-secondary whitespace-nowrap"
                    title={new Date(rc.createdAt).toISOString()}
                  >
                    {formatRelativeTime(rc.createdAt)}
                  </td>
                  <td className="text-text-secondary">
                    {rc.offerId == null
                      ? <span className="text-text-tertiary italic">unlinked</span>
                      : offerLookup.getName(rc.offerId)}
                  </td>
                  <td className="text-neon-cyan uppercase">{rc.eventType || 'sale'}</td>
                  <td className="text-right text-neon-yellow numeric">
                    {formatCurrency(rc.revenue)}
                  </td>
                  <td className="text-right"><StatusBadge status={rc.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
